import React, { useEffect, useRef, useState } from 'react';
import CeteleTemplateCatalog from '../../shared/CeteleTemplateCatalog';
import CeteleDeleteDialog from '../../shared/CeteleDeleteDialog';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TeacherStudentCard, { TeacherStudentAction, TeacherStudentGrid } from './TeacherStudentCard';
import TeacherAwardFeedback from './TeacherAwardFeedback';
import { CETELE_TEMPLATES, CETELE_ICONS, getControlNameSuggestions } from '../../shared/ceteleTemplates';
import './teacherCeteles.css';

const messages = {
  'A cetele with this name exists in the class': 'Bu sınıfta aynı adla bir çetele zaten var.',
  'A control with this title already exists': 'Bu başlıkta bir değerlendirme zaten var.',
  'Title cannot be blank': 'Başlık boş bırakılamaz.',
  'Control not found': 'Değerlendirme bulunamadı. Listeyi yenileyin.',
  'Cetele not found': 'Çetele bulunamadı. Listeyi yenileyin.',
  'Concurrent result change; reload before retrying': 'Kayıt değişti. Yenileyerek gerçek durumu kontrol edin.',
  'Control was created concurrently; reload and retry': 'Değerlendirmeler değişti. Listeyi yenileyin.',
};
const statuses = { pass: 'Öğrendi', retry: 'Tekrar', none: 'Kayıt yok' };
const filters = [['all', 'Tümü'], ['retry', 'Tekrar'], ['pass', 'Öğrendi'], ['none', 'Kayıt yok']];
const resultKey = (cetele, control, student) => `${cetele}:${control}:${student}`;
const statusFor = (detail, control, student) => detail.results.find(r => r.control_id === Number(control) && r.student_id === student)?.status || null;

export default function TeacherCeteles({ onRequest, classCode, onBack, onPendingChange }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [control, setControl] = useState('');
  const [tab, setTab] = useState('evaluation');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingMarks, setPendingMarks] = useState({});
  const [resultErrors, setResultErrors] = useState({});
  const [pointFeedback, setPointFeedback] = useState({});
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [focusAfterDelete, setFocusAfterDelete] = useState(false);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [template, setTemplate] = useState('english_words');
  const alive = useRef(false);
  const lock = useRef(false);
  const markLocks = useRef(new Set());
  const awardSequence = useRef(0);
  const primaryAction = useRef(null);
  const deleteFocus = useRef(false);
  const hasPending = busy || Object.keys(pendingMarks).length > 0;

  useEffect(() => {
    if (focusAfterDelete && !loading && !busy) {
      primaryAction.current?.focus(); setFocusAfterDelete(false);
    }
  }, [focusAfterDelete, loading, busy]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { onPendingChange?.(hasPending); }, [hasPending, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  useEffect(() => { setPointFeedback({}); }, [selected, control, reload, tab]);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setResultErrors({});
    onRequest(selected == null ? 'ceteles' : `ceteles/${selected}`)
      .then(data => {
        if (!active) return;
        if (selected == null) setItems(data.items);
        else {
          setDetail(data);
          setControl(current => data.controls.some(c => String(c.id) === current) ? current : String(data.controls.at(-1)?.id || ''));
        }
      })
      .catch(err => { if (active && err.status !== 401) setError(messages[err.message] || err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [onRequest, selected, reload]);

  const navigate = id => {
    if (lock.current || markLocks.current.size) return;
    setDetail(null); setControl(''); setSelected(id); setTab('evaluation'); setFilter('all');
  };
  const mutate = async operation => {
    if (lock.current || markLocks.current.size) return;
    lock.current = true; setBusy(true); setError('');
    try { await operation(); }
    catch (err) { if (alive.current && err.status !== 401) setError(messages[err.message] || err.message); }
    finally { lock.current = false; if (alive.current) setBusy(false); }
  };
  const write = (path, method, body) => onRequest(path, { method, body: JSON.stringify(body) });
  const showDialog = kind => {
    if (lock.current || markLocks.current.size) return;
    setError(''); setDialog(kind);
    if (kind === 'create') { setTemplate('english_words'); setTitle('Kelime Ezberi'); setSubject('İngilizce'); }
    else if (kind === 'rename') { setTitle(detail.title); setSubject(detail.subject); }
    else setTitle(kind === 'editControl' ? detail.controls.find(c => String(c.id) === control)?.title || '' : '');
  };
  const save = () => mutate(async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) return;
    if (dialog === 'create') {
      const result = await write('ceteles', 'POST', { title: cleanTitle, subject: subject.trim(), template_key: template });
      if (!alive.current) return;
      setDetail(null); setControl(''); setSelected(result.id); setTab('evaluation'); setFilter('all');
    } else if (dialog === 'rename') {
      await write(`ceteles/${selected}`, 'PATCH', { title: cleanTitle, subject: subject.trim() });
      if (!alive.current) return;
      setDetail(current => ({ ...current, title: cleanTitle, subject: subject.trim() }));
    } else {
      const result = await write(`ceteles/${selected}/controls${dialog === 'editControl' ? '/' + control : ''}`,
        dialog === 'editControl' ? 'PATCH' : 'POST', { title: cleanTitle });
      if (!alive.current) return;
      setLoading(true);
      if (dialog === 'control') { setControl(String(result.id)); setFilter('all'); setTab('evaluation'); }
      setReload(value => value + 1);
    }
    if (alive.current) setDialog(null);
  });
  const beginDelete = target => {
    if (lock.current || markLocks.current.size) return;
    setError(''); setDeleteTarget(target);
  };
  const confirmDelete = () => mutate(async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    await write(`ceteles/${target.ceteleId}${target.kind === 'control' ? '/controls/' + target.id : ''}`, 'DELETE');
    if (!alive.current) return;
    if (target.kind === 'cetele') {
      setItems(previous => previous.filter(item => item.id !== target.ceteleId));
      if (selected === target.ceteleId) {
        setDetail(null); setControl(''); setSelected(null);
      }
    } else {
      const remaining = detail.controls.filter(c => c.id !== target.id);
      setDetail(previous => ({ ...previous, controls: remaining,
        results: previous.results.filter(r => r.control_id !== target.id) }));
      setControl(String(remaining.at(-1)?.id || '')); setFilter('all');
      setPointFeedback({}); setResultErrors({});
    }
    deleteFocus.current = true; setDeleteTarget(null);
  });
  const mark = async (student, value) => {
    if (lock.current || loading || !detail || !control) return;
    const ceteleId = selected;
    const controlId = Number(control);
    const key = resultKey(ceteleId, controlId, student);
    if (markLocks.current.has(key)) return;
    markLocks.current.add(key);
    setPendingMarks(current => ({ ...current, [key]: value }));
    setPointFeedback(current => { const next = { ...current }; delete next[key]; return next; });
    setResultErrors(current => { const next = { ...current }; delete next[key]; return next; });
    setError('');
    const before = statusFor(detail, controlId, student);
    const status = before === value ? null : value;
    try {
      const result = await write(`ceteles/${ceteleId}/controls/${controlId}/students/${student}`, 'PUT', { status });
      if (!alive.current) return;
      if (result.control_id !== controlId || result.student_id !== student || result.status !== status) {
        throw new Error('Kayıt doğrulanamadı. Yenileyerek gerçek durumu kontrol edin.');
      }
      setDetail(current => current?.id !== ceteleId ? current : ({ ...current, results: [
        ...current.results.filter(r => !(r.control_id === controlId && r.student_id === student)),
        ...(status ? [result] : []),
      ] }));
      if (status === 'pass' && result.awarded_points === 2) {
        const sequence = ++awardSequence.current;
        setPointFeedback(current => ({ ...current, [key]: sequence }));
      }
    } catch (err) {
      if (alive.current && err.status !== 401) setResultErrors(current => ({ ...current, [key]: messages[err.message] || err.message }));
    } finally {
      markLocks.current.delete(key);
      if (alive.current) setPendingMarks(current => { const next = { ...current }; delete next[key]; return next; });
    }
  };

  const current = detail?.controls.find(c => String(c.id) === control);
  const counts = detail ? detail.students.reduce((all, student) => {
    all[statusFor(detail, control, student.id) || 'none'] += 1; return all;
  }, { pass: 0, retry: 0, none: 0 }) : {};
  const visibleStudents = detail?.students.filter(s => filter === 'all' || (statusFor(detail, control, s.id) || 'none') === filter) || [];
  const suggestions = getControlNameSuggestions(detail?.template_key, detail?.controls || []);
  const namingCetele = dialog === 'create' || dialog === 'rename';

  return <Box className="teacher-ceteles">
    <Button startIcon={<ArrowBackRoundedIcon />} disabled={hasPending} onClick={selected == null ? onBack : () => navigate(null)}>
      {selected == null ? 'Öğretmen Moduna Dön' : 'Çetelelerime Dön'}
    </Button>
    <Paper className="tc-header" elevation={0}>
      <Box sx={{ minWidth: 0 }}>
        <Typography className="tc-eyebrow">{classCode} · Öğretmen Modu</Typography>
        <Typography component="h1">{selected == null ? 'Çetelelerim' : detail?.title || 'Çetele yükleniyor…'}</Typography>
        <Typography>{selected == null ? 'Öğrenilenleri ve tekrar ihtiyaçlarını sınıfınla birlikte takip et.' : detail?.subject}</Typography>
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {detail && <Button variant="outlined" disabled={hasPending || loading} onClick={() => showDialog('rename')}>Çeteleyi Düzenle</Button>}
        {detail && <Button variant="outlined" className="tc-delete" startIcon={<DeleteOutlineRoundedIcon />}
          disabled={hasPending || loading} onClick={() => beginDelete({ kind: 'cetele', ceteleId: detail.id, title: detail.title })}>Çeteleyi Sil</Button>}
        <Button ref={primaryAction} variant="contained" startIcon={<AddRoundedIcon />} disabled={hasPending || loading || Boolean(selected && !detail)}
          onClick={() => showDialog(selected == null ? 'create' : 'control')}>
          {selected == null ? 'Yeni Çetele' : 'Yeni Değerlendirme'}
        </Button>
      </Stack>
    </Paper>
    {error && !dialog && !deleteTarget && <Alert severity="error" sx={{ my: 2 }}>{error}
      <Button disabled={hasPending} onClick={() => setReload(v => v + 1)}>Yenile</Button></Alert>}
    {loading ? <Box role="status" className="tc-empty"><CircularProgress size={28} /> Çeteleler yükleniyor…</Box>
      : selected == null ? <>
        <Box className="tc-list">
          {items.map(item => <Paper key={item.id} className="tc-list-card" elevation={0}>
            <Typography aria-hidden="true" className="tc-template-icon">{CETELE_ICONS[item.template_key] || '📋'}</Typography>
            <Typography component="h2">{item.title}</Typography>
            <Typography>{item.subject} · {item.control_count} değerlendirme</Typography>
            <Typography>Son değerlendirme: {item.last_control || 'Henüz eklenmedi'}</Typography>
            <Button variant="contained" onClick={() => navigate(item.id)} aria-label={`Çeteleyi Aç: ${item.title}`}>Çeteleyi Aç</Button>
            <Button className="tc-delete" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />}
              aria-label={`Çeteleyi Sil: ${item.title}`} disabled={hasPending}
              onClick={() => beginDelete({ kind: 'cetele', ceteleId: item.id, title: item.title })}>Çeteleyi Sil</Button>
          </Paper>)}
        </Box>
        {!error && !items.length && <Paper className="tc-empty" elevation={0}>Bu sınıfta henüz çetele yok. Yeni Çetele ile hazır bir şablon seçebilir veya kendi çeteleni oluşturabilirsin.</Paper>}
        <Typography className="tc-help">Kaydedilen sonuçlar veliler tarafından Öğrenci360’ta görülebilir. Sonuç değişiklikleri mevcut veli bildirim akışına iletilir.</Typography>
      </> : detail && <>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth" aria-label="Çetele görünümü">
          <Tab value="evaluation" label="Değerlendir" disabled={hasPending} id="tc-evaluation-tab" aria-controls="tc-panel" />
          <Tab value="general" label="Genel Çetele" disabled={hasPending} id="tc-general-tab" aria-controls="tc-panel" />
        </Tabs>
        <Box role="tabpanel" id="tc-panel" aria-labelledby={`tc-${tab}-tab`} sx={{ mt: 2 }}>
          {tab === 'evaluation' ? <>
            <Paper className="tc-toolbar" elevation={0}>
              <TextField select label="Değerlendirme" value={control} disabled={hasPending || !detail.controls.length}
                onChange={e => { setControl(e.target.value); }} sx={{ flex: 1, minWidth: 0 }}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxWidth: 'calc(100vw - 24px)', '& .MuiMenuItem-root': { minHeight: 48, whiteSpace: 'normal', overflowWrap: 'anywhere' } } } } }}>
                {detail.controls.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.title}</MenuItem>)}
              </TextField>
              <Button variant="outlined" disabled={!current || hasPending} onClick={() => showDialog('editControl')}>Başlığı Düzenle</Button>
              <Button className="tc-delete" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />}
                disabled={!current || hasPending} onClick={() => beginDelete({ kind: 'control', ceteleId: detail.id, id: current.id, title: current.title })}>Değerlendirmeyi Sil</Button>
            </Paper>
            {!current ? <Paper className="tc-empty" elevation={0}>Henüz değerlendirme yok. Yeni Değerlendirme ile ilk başlığı ekle.</Paper> : <>
              <Box className="tc-filters" role="group" aria-label="Öğrenci filtreleri">
                {filters.map(([key, label]) => <Button key={key} aria-pressed={filter === key} disabled={hasPending}
                  variant={filter === key ? 'contained' : 'outlined'} onClick={() => setFilter(key)}>
                  {label} · {key === 'all' ? detail.students.length : counts[key]}
                </Button>)}
              </Box>
              <Typography className="tc-help">{counts.pass + counts.retry} / {detail.students.length} öğrenci değerlendirildi. Sonuçlar otomatik kaydedilir. Seçili sonuca tekrar dokunmak kaydı temizler.</Typography>
              <TeacherStudentGrid label="Öğrencilerin çetele değerlendirmesi" busy={busy}>
                {visibleStudents.map(student => {
                  const status = statusFor(detail, control, student.id);
                  const key = resultKey(selected, control, student.id);
                  const saving = Boolean(pendingMarks[key]);
                  return <TeacherStudentCard key={student.id} busy={saving} studentId={student.id} name={student.name}
                    avatarOverlay={pointFeedback[key] && <TeacherAwardFeedback key={pointFeedback[key]} label="⭐ +2" studentName={student.name} />}
                    avatarId={student.avatar_id} starterEmoji={student.starter_emoji}
                    statusLabel={statuses[status || 'none']} tone={status === 'pass' ? 'success' : status === 'retry' ? 'warning' : 'neutral'}
                    actionColumns={2} actionsLabel={`${student.name} çetele durumu`}
                    actions={<>
                      <TeacherStudentAction loading={pendingMarks[key] === 'retry'} className="tc-retry" aria-label={`Tekrar: ${student.name}`} aria-pressed={status === 'retry'}
                        variant={status === 'retry' ? 'contained' : 'outlined'} disabled={busy || saving} startIcon={<ReplayRoundedIcon />} disableRipple
                        onClick={() => mark(student.id, 'retry')}>Tekrar</TeacherStudentAction>
                      <TeacherStudentAction loading={pendingMarks[key] === 'pass'} className="tc-pass" aria-label={`Öğrendi: ${student.name}`} aria-pressed={status === 'pass'}
                        variant={status === 'pass' ? 'contained' : 'outlined'} disabled={busy || saving} startIcon={<CheckCircleRoundedIcon />} disableRipple
                        onClick={() => mark(student.id, 'pass')}>Öğrendi</TeacherStudentAction>
                    </>}>
                    {resultErrors[key] && <Alert severity="error" className="tc-result-error">
                      {resultErrors[key]}
                      <Button disabled={hasPending} onClick={() => setReload(v => v + 1)}>Yenile</Button>
                    </Alert>}
                  </TeacherStudentCard>;
                })}
              </TeacherStudentGrid>
              {!visibleStudents.length && <Paper className="tc-empty" role="status" elevation={0}>
                {detail.students.length ? 'Bu filtreyle eşleşen öğrenci yok.' : 'Bu sınıfta aktif öğrenci yok.'}
              </Paper>}
            </>}
          </> : <>
            <TeacherStudentGrid label="Öğrencilerin genel çetelesi">
              {detail.students.map(student => {
                const recorded = detail.controls.filter(c => statusFor(detail, c.id, student.id)).length;
                return <TeacherStudentCard key={student.id} studentId={student.id} name={student.name}
                  avatarId={student.avatar_id} starterEmoji={student.starter_emoji}
                  statusLabel={detail.controls.length ? `${recorded} / ${detail.controls.length} değerlendirildi` : 'Henüz değerlendirme yok'}>
                  {detail.controls.length > 0 && <Box component="dl" className="tc-summary-results" aria-label={`${student.name} değerlendirme sonuçları`}>
                    {detail.controls.map(c => {
                      const status = statusFor(detail, c.id, student.id);
                      const tone = status === 'pass' ? 'success' : status === 'retry' ? 'warning' : 'neutral';
                      return <Box key={c.id} className="tc-summary-row">
                        <Typography component="dt" className="tc-summary-title">{c.title}</Typography>
                        <Box component="dd"><span className={`teacher-student-card__status teacher-student-card__status--${tone}`}>
                          {statuses[status || 'none']}
                        </span></Box>
                      </Box>;
                    })}
                  </Box>}
                </TeacherStudentCard>;
              })}
            </TeacherStudentGrid>
            {!detail.students.length && <Paper className="tc-empty" role="status" elevation={0}>Bu sınıfta aktif öğrenci yok.</Paper>}
          </>}
        </Box>
      </>}
    <CeteleDeleteDialog target={deleteTarget} busy={hasPending} error={error} teacher
      onClose={() => { setDeleteTarget(null); setError(''); }} onConfirm={confirmDelete} onExited={() => {
        if (deleteFocus.current) { setFocusAfterDelete(true); deleteFocus.current = false; }
      }} />
    <Dialog open={Boolean(dialog)} onClose={() => !hasPending && setDialog(null)} fullWidth maxWidth={dialog === 'create' ? 'md' : 'sm'}
      aria-labelledby="tc-dialog-title" PaperProps={{ className: 'teacher-ceteles tc-dialog' }}>
      <Box component="form" onSubmit={e => { e.preventDefault(); if (!hasPending && title.trim() && (!namingCetele || subject.trim())) save(); }}>
        <DialogTitle id="tc-dialog-title">{{ create: 'Yeni Çetele', rename: 'Çeteleyi Düzenle', control: 'Yeni Değerlendirme', editControl: 'Başlığı Düzenle' }[dialog]}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Typography>{classCode} sınıfı için {namingCetele ? 'çetele' : 'değerlendirme'}.</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {dialog === 'create' && <>
            <Typography component="h2">Nasıl başlamak istersiniz?</Typography>
            <Typography>Hazır bir seçenek seçebilir veya tamamen kendinize ait bir çetele oluşturabilirsiniz.</Typography>
            <CeteleTemplateCatalog autoFocus value={template} disabled={hasPending} onChange={id => {
              const next = CETELE_TEMPLATES.find(t => t.id === id);
              setTemplate(next.id); setTitle(next.title); setSubject(next.subject);
            }} />
          </>}
          <Box className={dialog === 'create' ? 'tc-create-fields' : undefined} sx={{ display: 'grid', gap: 2 }}>
            {dialog === 'create' && <Typography component="h2">Çetele bilgileri</Typography>}
            <TextField autoFocus={dialog !== 'create'} label={namingCetele ? 'Çetele Adı' : 'Değerlendirme Başlığı'} value={title} disabled={hasPending}
              onChange={e => setTitle(e.target.value)} inputProps={{ maxLength: namingCetele ? 160 : 120 }} required />
            {namingCetele && <TextField label="Ders" value={subject} disabled={hasPending} onChange={e => setSubject(e.target.value)} inputProps={{ maxLength: 100 }} required />}
          </Box>
          {dialog === 'control' && suggestions.length > 0 && <Box>
            <Typography sx={{ mb: 1 }}>Başlık önerileri</Typography><Stack direction="row" flexWrap="wrap" gap={1}>
              {suggestions.map(s => <Button key={s} variant="outlined" disabled={hasPending} onClick={() => setTitle(s)}>{s}</Button>)}
            </Stack></Box>}
        </DialogContent>
        <DialogActions><Button disabled={hasPending} onClick={() => setDialog(null)}>Vazgeç</Button>
          <Button className={dialog === 'create' ? 'tc-create-submit' : undefined} variant="contained" type="submit" disabled={hasPending || !title.trim() || (namingCetele && !subject.trim())}>{busy ? 'Kaydediliyor…' : dialog === 'create' ? 'Çeteleyi Oluştur' : 'Kaydet'}</Button></DialogActions>
      </Box>
    </Dialog>
  </Box>;
}
